<template>
  <Teleport to="body">
    <div v-if="visible" class="drawer-backdrop" @click="visible = false" />
    <aside v-if="visible" class="drawer">
      <!-- Header -->
      <div class="drawer-header">
        <span
          >Filtres<template v-if="!areFiltersEmpty">
            · {{ activeFiltersCount }}</template
          ></span
        >
        <div style="display: flex; gap: 8px; align-items: center">
          <button
            class="btn btn-primary"
            style="padding: 6px 12px; font-size: 0.75rem"
            :disabled="areFiltersEmpty"
            @click="emit('openSaveDialog')"
          >
            Sauvegarder
          </button>
          <button class="close-btn" @click="visible = false">✕</button>
        </div>
      </div>

      <!-- Body -->
      <div class="drawer-body">
        <!-- Departments -->
        <div class="drawer-section">
          <span class="drawer-section-title">Départements</span>
          <DepartmentSelect v-model="localDepts" />
        </div>

        <!-- Duration -->
        <div class="drawer-section">
          <span class="drawer-section-title">Durée</span>
          <div style="display: flex; gap: 6px; flex-wrap: wrap">
            <button
              v-for="opt in durationOpts"
              :key="opt.value"
              class="chip"
              :class="{ 'chip-active': duration === opt.value }"
              @click="duration = opt.value"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>

        <!-- Date range -->
        <div class="drawer-section">
          <span class="drawer-section-title">Date min</span>
          <DatePicker
            v-model="startDate"
            :min="today"
            placeholder="jj/mm/aaaa"
          />
        </div>
        <div class="drawer-section">
          <span class="drawer-section-title">Date max</span>
          <DatePicker
            v-model="endDate"
            :min="startDate || today"
            placeholder="jj/mm/aaaa"
          />
        </div>

        <!-- Source -->
        <div class="drawer-section">
          <span class="drawer-section-title">Source</span>
          <SelectField v-model="source" :options="sourceOpts" />
        </div>

        <!-- Incomplete address (admin) -->
        <div v-if="user" class="drawer-section">
          <span class="drawer-section-title">Adresse incomplète</span>
          <SelectField v-model="incompleteAddress" :options="incompleteOpts" />
        </div>

        <!-- Saved searches -->
        <div v-if="savedSearches.length > 0" class="drawer-section">
          <span class="drawer-section-title">Recherches sauvegardées</span>
          <div style="display: flex; flex-direction: column; gap: 6px">
            <button
              v-for="(search, key) in savedSearches"
              :key="key"
              class="btn"
              style="justify-content: space-between; width: 100%"
              @click="emit('loadSearch', search.filters)"
            >
              <span>{{ search.label }}</span>
              <span @click.stop="emit('deleteSearch', key)">✕</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Footer actions -->
      <div class="drawer-footer">
        <button class="btn" style="width: 100%" @click="emit('reset')">
          Réinitialiser les filtres
        </button>
        <button
          class="btn btn-primary"
          style="width: 100%"
          @click="visible = false"
        >
          Appliquer la recherche
        </button>
      </div>
    </aside>
  </Teleport>
</template>

<script setup lang="ts">
import {
  eventDurations,
  sourceValues,
  incompleteAddressValues,
} from '@/types/Event';
import type { EventFilters, SavedSearch } from '@/types/Event';

const visible = defineModel<boolean>('visible', { required: true });

useEscapeKey(visible, () => {
  visible.value = false;
});
const selectedDepartements = defineModel<string[]>('selectedDepartements');
const duration = defineModel<string>('duration');
const startDate = defineModel<Date>('startDate');
const endDate = defineModel<Date>('endDate');
const source = defineModel<string>('source', { default: '' });
const incompleteAddress = defineModel<string>('incompleteAddress', {
  default: '',
});

defineProps<{
  savedSearches: SavedSearch[];
}>();

const { areFiltersEmpty, activeFiltersCount } = useFilters();

const emit = defineEmits<{
  reset: [];
  openSaveDialog: [];
  loadSearch: [filters: EventFilters];
  deleteSearch: [index: number];
}>();

const user = useSupabaseUser();
const today = new Date();

const localDepts = computed<string[]>({
  get: () => selectedDepartements.value ?? [],
  set: (v) => {
    selectedDepartements.value = v;
  },
});

const durationOpts = eventDurations;
const sourceOpts = [
  { value: 'ALL', label: 'Tous' },
  ...sourceValues
    .filter((s) => s.value !== 'ALL')
    .map((s) => ({ value: s.value, label: s.label })),
];
const incompleteOpts = incompleteAddressValues;
</script>
